import {useCallback, useEffect} from 'react';
import {app, authentication, HostClientType} from '@microsoft/teams-js';
import {getIsMsTeamsEnv} from '@lib/infrastructure';
import {
  disconnectCalendar,
  enableCalendarAfterRedirection,
  setIsSigningOut,
  showTimedFeedback,
  signInPopUp,
  signOut,
  signOutPopup,
  silentSignIn,
  useAppDispatch,
  withAsyncThunkErrorHandling,
} from '@lib/store';
import {useMsal} from '@azure/msal-react';

export const useIsTeamsApp = () => {
  return getIsMsTeamsEnv();
};

export const useMsTeams = () => {
  const isTeamsApp = useIsTeamsApp();

  useEffect(() => {
    const initTeams = async () => {
      try {
        await app.initialize();
        console.debug('Teams SDK initialized');
        app.notifySuccess();
      } catch (error) {
        console.debug('Teams SDK initialization failed:', error);
      }
    };

    if (isTeamsApp) {
      initTeams();
    }
  }, [isTeamsApp]);
};

export const useTeamSignIn = () => {
  const dispatch = useAppDispatch();
  const {instance} = useMsal();
  const isTeamsApp = useIsTeamsApp();

  const login = useCallback(async () => {
    if (!isTeamsApp) return;

    try {
      const clientType = (await app.getContext()).app.host.clientType;
      if (clientType === HostClientType.web) {
        dispatch(withAsyncThunkErrorHandling(() => signInPopUp()));
        return;
      }

      // desktop app
      const result = await authentication.authenticate({
        url: window.location.origin + '/teams-signin',
        width: 600,
        height: 535,
      });

      const {msalAccount, localStorage: ls} = JSON.parse(result);

      // This seems fishy, but let me explain:
      // The authentication flow is supposed to work like this:
      // 1. User initiates login in Teams desktop app
      // 2. A popup window opens to our /teams-signin page
      // 3. The MSAL instance in that page handles the redirect and login
      // 4. Upon successful login, the popup sets up localStorage and returns the account info
      // 5. In the main window we can set the active account on the MSAL instance, which will use the information stored in the localStorage
      // This works like this on the Microsoft Teams desktop app, but on the Copilot desktop app and the Outlook desktop app, the localstorage that is set in the popup is not shared with the localstorage in the main window (it seems).
      // Thus as workaround, we manually copy over the localstorage items from the popup to the main window's localstorage.
      for (const [key, value] of Object.entries(ls ?? {})) {
        // localStorage.setItem expects a string; ensure we pass a string by using the value if it's already a string
        // or JSON.stringify for other types.
        if (typeof value === 'string') {
          localStorage.setItem(key, value);
        } else {
          localStorage.setItem(key, JSON.stringify(value));
        }
      }

      instance.setActiveAccount(JSON.parse(result));
      // this will setup all the store tokens and flags for user logged in
      dispatch(withAsyncThunkErrorHandling(() => silentSignIn()));
    } catch (err) {
      const error = err as Error;
      console.debug('Auth failed:', error);

      if ((error as Error).message !== 'CancelledByUser') {
        dispatch(showTimedFeedback({message: error.message, type: 'error'}));
      }
    }
  }, [dispatch, instance, isTeamsApp]);

  return login;
};

export const useTeamsSignOut = () => {
  const dispatch = useAppDispatch();
  const isTeamsApp = useIsTeamsApp();

  const logout = useCallback(async () => {
    if (!isTeamsApp) return;

    dispatch(setIsSigningOut(true));

    const clientType = (await app.getContext()).app.host.clientType;
    if (clientType === HostClientType.web) {
      dispatch(signOutPopup());
      return;
    }

    // desktop
    try {
      const result = await authentication.authenticate({
        url: window.location.origin + '/teams-signout',
        width: 600,
        height: 535,
      });
      // Loop over each local storage entry and if it isn't in the result after signout in the popup, remove it
      const {localStorage: ls} = JSON.parse(result);

      for (const key of Object.keys(localStorage)) {
        if (!ls || !(key in ls)) {
          localStorage.removeItem(key);
        }
      }
    } finally {
      dispatch(signOut({msteams: true}));
    }
  }, [dispatch, isTeamsApp]);

  return logout;
};

export const useTeamsMeetConsent = () => {
  const dispatch = useAppDispatch();
  const isTeamsApp = useIsTeamsApp();

  const askForConsent = useCallback(async () => {
    if (!isTeamsApp) return;

    try {
      const result = await authentication.authenticate({
        url: '/teams-calendar-connect?isTeams=true',
        width: 600,
        height: 535,
      });

      if (result === 'success') {
        await dispatch(withAsyncThunkErrorHandling(() => enableCalendarAfterRedirection()));
      } else {
        dispatch(disconnectCalendar());
      }
    } catch (error) {
      console.debug('askForConsent failed:', error);
      dispatch(disconnectCalendar());
    }
  }, [dispatch, isTeamsApp]);

  return {askForConsent};
};
